import type { DisplayFilters } from '@wix/component-protocol/schema';
import { ACTIONS } from '@wix/component-protocol/schema';

import { ManifestComponentExtensionType } from '../../stores';

import {
  VirtualElement,
  VirtualElementAPIType,
} from '../providers/VirtualElement';
import { AbstractManifestAPI } from '../AbstractManifestAPI';
import { CacheMethodResponse } from '../decorators';
import { MethodCacheStrategy } from '../MethodCacheStrategy';
import { IManifestChangeDetection } from '../../ManifestChangeDetection';
import { ManifestServiceFlags } from '../../ManifestServiceFlags';
import { ManifestServiceStats } from '../../ManifestServiceStats';
import { ManifestResolverAPI } from '../../handlers';
import type { EditorPointerServiceAPI } from '@wix/editor-document-logic-api';

import { ManifestDeprecationResolver } from './ManifestDeprecationResolver';

export class ManifestDisplayFilterResolver extends AbstractManifestAPI<
  ManifestResolverAPI,
  VirtualElementAPIType.ManifestResolver
> {
  private readonly deprecationResolver: ManifestDeprecationResolver;

  constructor(
    node: VirtualElement<VirtualElementAPIType.ManifestResolver>,
    api: ManifestResolverAPI,
    editorPointerAPI: EditorPointerServiceAPI,
    manifestChangeDetection: IManifestChangeDetection,
    manifestServiceFlags: ManifestServiceFlags,
    manifestServiceStats: ManifestServiceStats,
  ) {
    super(
      node,
      api,
      editorPointerAPI,
      manifestChangeDetection,
      manifestServiceFlags,
      manifestServiceStats,
    );

    this.deprecationResolver = new ManifestDeprecationResolver(
      this.node,
      this.api,
      this.editorPointerAPI,
      this.manifestChangeDetection,
      this.manifestServiceFlags,
      this.manifestServiceStats,
    );
  }

  /**
   * TODO: decrease number of params, 100% we should NOT pass extension type as param
   */
  private isKeyVisibleByDisplayFilter(
    key: string,
    displayFilter: DisplayFilters[keyof DisplayFilters],
    extensionType: ManifestComponentExtensionType,
  ) {
    if (this.isHiddenByDefault(key, displayFilter, extensionType)) {
      return false;
    }

    if (!displayFilter) {
      return true;
    }

    if (displayFilter.show?.length) {
      return displayFilter.show.includes(key);
    }

    if (displayFilter.hide?.length) {
      return !displayFilter.hide.includes(key);
    }

    return true;
  }

  @CacheMethodResponse({
    namespace: 'ManifestDisplayFilterResolver',
    cacheStrategy: MethodCacheStrategy.PresetOrStateUpdate,
  })
  getEditableItems() {
    const definitionKeys = [
      'cssProperties',
      'cssCustomProperties',
      'data',
      'customActions',
      'states',
      'actions',
      'presets',
    ] as const;

    const keys = [...definitionKeys, 'elements'] as const;

    const contexts = this.virtualManifestOperations.getEditorPointers(
      ...definitionKeys,
    );

    const items: Record<(typeof keys)[number], Set<string>> = {
      cssProperties: new Set(),
      cssCustomProperties: new Set(),
      data: new Set(),
      customActions: new Set(),
      states: new Set(),
      actions: new Set(),
      presets: new Set(),
      elements: new Set(),
    };

    contexts.forEach((context) => {
      const contextNode = this.node.extract(context.editorPointer);
      const _api = contextNode.getAPI(
        VirtualElementAPIType.VirtualManifestAPIReadonly,
      );

      /**
       * Resolve deprecation against the per-context manifest, not the merged
       * root one: reducers keep a single source's definition object, so the
       * merged `deprecated` flag is selection-order dependent and a key
       * deprecated in this component could otherwise leak through in
       * multi-selection.
       */
      const contextEditorElement =
        contextNode.getVirtualManifest().tree.manifest.editorElement;

      const preset = _api.query.getAppliedPreset();
      const state = _api.query.getState();
      const designStates = this.manifestServiceFlags.isSpecEnabled(
        'specs.editor-platform.design-states',
      )
        ? _api.query.getDesignState()
        : [];

      definitionKeys.forEach((key) => {
        const mappings = this.virtualManifestOperations.getMappings(
          context.editorPointer,
          key,
        );

        /**
         * guess is not critical for now,
         * but seems we need to get display filters per component,
         * otherwise we may face some edge cases bugs
         * when there are intersections in display filters from multiple components
         *
         * -->
         * const displayFilters = _api.resolver.getDisplayFilters(key);
         */
        const displayFilters = this.resolveDisplayFilters(
          key,
          preset,
          state,
          designStates,
        );

        Object.keys(mappings)
          .filter((_key) => {
            if (
              this.deprecationResolver.isDeprecatedDefinition(
                contextEditorElement,
                key,
                _key,
              )
            ) {
              return false;
            }

            return this.isKeyVisibleByDisplayFilter(
              _key,
              displayFilters,
              context.extensionType,
            );
          })
          .forEach((_key) => {
            items[key].add(_key);
          });
      });
    });

    const query = this.node.getAPI(VirtualElementAPIType.ManifestQueryReadonly);

    const preset = query.getAppliedPreset();
    const state = query.getState();
    const designStates = this.manifestServiceFlags.isSpecEnabled(
      'specs.editor-platform.design-states',
    )
      ? query.getDesignState()
      : [];

    const displayFilters = this.resolveDisplayFilters(
      'elements',
      preset,
      state,
      designStates,
    );

    this.virtualManifest.tree.children
      .filter((child) => {
        if (this.deprecationResolver.isDeprecatedChild(child)) {
          return false;
        }

        return this.isKeyVisibleByDisplayFilter(
          child.elementName!,
          displayFilters,
          child.extensionType!,
        );
      })
      .forEach((child) => {
        items.elements.add(child.elementName!);
      });

    return items;
  }

  private resolveDisplayFilters(
    filterType: keyof DisplayFilters,
    preset: string | null,
    state: string | null,
    designStates: string[] = [],
  ) {
    const editorElement = this.virtualManifest.tree.manifest.editorElement;

    const baseDisplayFilter = editorElement?.displayFilters?.[filterType];

    const presetDisplayFilter = this.getPresetDisplayFilter(
      this.node.isMultipleComponentsRoot()
        ? ManifestComponentExtensionType.BUILDER
        : this.node.getExtensionType(),
      filterType,
      preset,
    );

    const stateDisplayFilter = state
      ? editorElement?.states?.[state]?.displayFilters?.[filterType]
      : undefined;

    const designStateDisplayFilters = this.manifestServiceFlags.isSpecEnabled(
      'specs.editor-platform.design-states',
    )
      ? designStates
          .map(
            (ds) => editorElement?.states?.[ds]?.displayFilters?.[filterType],
          )
          .filter((f): f is NonNullable<typeof f> => !!f)
      : [];

    const filters = [
      ...designStateDisplayFilters,
      baseDisplayFilter,
      presetDisplayFilter,
      stateDisplayFilter,
    ].filter((x) => !!x);

    if (!filters.length) {
      return undefined;
    }

    const result: DisplayFilters[keyof DisplayFilters] = {};

    /**
     * A `hide` declared on the base displayFilter is authoritative: this is
     * where dynamic overrides land (see DynamicOverridesMiddleware), and a
     * runtime, state-driven `hide` must win over a broad `show` coming from a
     * preset/state. Keys the base itself also lists in `show` are exempt, so
     * same-source `show` still wins (preserves existing behavior).
     */
    const baseAuthoritativeHide = (baseDisplayFilter?.hide ?? []).filter(
      (key) => !(baseDisplayFilter?.show ?? []).includes(key),
    );

    const mergedShow = filters
      .flatMap((f) => f.show ?? [])
      .filter((key) => !baseAuthoritativeHide.includes(key));

    if (mergedShow.length) {
      result.show = [...new Set(mergedShow)];
    }

    const mergedHide = filters.flatMap((f) => f.hide ?? []);

    if (mergedHide.length) {
      result.hide = [...new Set(mergedHide)];
    }

    return Object.keys(result).length ? result : undefined;
  }

  getDisplayFilters<K extends keyof DisplayFilters>(
    filterType: K,
  ): DisplayFilters[K] | undefined {
    const _api = this.node.getAPI(VirtualElementAPIType.ManifestQueryReadonly);
    const designStates = this.manifestServiceFlags.isSpecEnabled(
      'specs.editor-platform.design-states',
    )
      ? _api.getDesignState()
      : [];

    return this.resolveDisplayFilters(
      filterType,
      _api.getAppliedPreset(),
      _api.getState(),
      designStates,
    );
  }

  private isHiddenByDefault(
    key: string,
    displayFilter: DisplayFilters[keyof DisplayFilters],
    extensionType: ManifestComponentExtensionType,
  ) {
    const isOOI = extensionType === ManifestComponentExtensionType.OOI;
    const isDesignAction = key === ACTIONS.ACTION_NAME.design;

    return isDesignAction && isOOI && !displayFilter;
  }

  private getPresetDisplayFilter(
    /**
     * TODO: remove this argument
     */
    extensionType: ManifestComponentExtensionType,
    filterType: keyof DisplayFilters,
    preset: string | null,
  ): DisplayFilters[typeof filterType] {
    const editorElement = this.virtualManifest.tree.manifest.editorElement;

    if (extensionType === ManifestComponentExtensionType.OOI) {
      const statics =
        this.virtualManifest.manifests[0]?.store?.metadata?.statics;

      if (statics && preset) {
        const displayFilters = statics.presetsPanel?.find(
          ({ id }) => id === preset,
        )?.displayFilters as unknown as DisplayFilters | undefined;

        const staticsFilter = displayFilters?.[filterType];

        if (staticsFilter) {
          return staticsFilter;
        }
      }
    }

    return preset
      ? editorElement?.presets?.[preset]?.presetDefaults?.displayFilters?.[
          filterType
        ]
      : undefined;
  }
}
